// apps/web/src/components/users/permission-matrix.tsx — Fase 15, Plano 07 (GOV-04); quick 261006-lf9; redesenho de /admin/perfis.
// A matriz só edita ações do catálogo do servidor. Grants anteriores que não
// pertencem ao catálogo continuam no servidor e aparecem como contexto parcial.
// Rótulos e descrições vêm de lib/permissoes-catalogo (fonte única em pt-BR).
// Módulos em acordeão (com contador e tri-state), busca, filtro "só concedidas" e
// barra de salvar: fixa no diálogo de /admin/users, só com alterações no painel de /admin/perfis.
import { ChevronRight, Info, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';
import { agruparPorModulo } from '../../lib/permissoes-catalogo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  chavesAlteradas,
  contagemDoModulo,
  descartarAlteracoes,
  filtrarPermissoes,
  filtroAtivo,
  marcarTodas,
  modulosAbertosIniciais,
  rotuloAlteracoes,
  type EstadoDoModulo,
} from './permission-matrix-estado';

export interface PermissionMatrixItem {
  readonly key: string;
  readonly module: string;
  readonly action: string;
}

/**
 * 'dialogo' (padrão, /admin/users): cartão com título e rodapé de salvar sempre visível.
 * 'painel' (/admin/perfis): sem moldura; a barra de salvar fica grudada no rodapé do painel e só aparece com alterações.
 */
export type VarianteMatriz = 'dialogo' | 'painel';

export interface PermissionMatrixProps {
  readonly roleId: string;
  readonly roleName: string;
  readonly onSaveSuccess?: () => void;
  readonly variante?: VarianteMatriz;
  /** Avisa quantas alterações ainda não foram salvas (0 ao salvar, recarregar ou desmontar). */
  readonly onAlteracoes?: (quantidade: number) => void;
}

export interface PermissionMatrixContentProps {
  readonly roleName: string;
  readonly catalog: readonly PermissionMatrixItem[];
  readonly grants: Readonly<Record<string, boolean>>;
  readonly partialGrants: readonly string[];
  readonly error?: string | null;
  readonly saving: boolean;
  readonly saveEnabled: boolean;
  /** Verdadeiro logo depois de um salvamento bem-sucedido, até o próximo toggle. */
  readonly salvo?: boolean;
  /** Padrão: users:manage marcado num perfil que não se chama Admin. */
  readonly avisoAdministrativo?: boolean;
  readonly variante?: VarianteMatriz;
  /** Quantidade de permissões alteradas e ainda não salvas. */
  readonly alteracoes?: number;
  readonly buscaInicial?: string;
  readonly soConcedidasInicial?: boolean;
  readonly onToggle: (key: string) => void;
  /** Marca ou desmarca de uma vez as permissões exibidas de um módulo. */
  readonly onToggleModulo?: (keys: readonly string[], marcar: boolean) => void;
  readonly onSave: () => void;
  readonly onDescartar?: () => void;
  readonly onReload?: () => void;
}

const LOAD_ERROR = 'Não foi possível carregar os dados. Verifique a conexão e tente novamente.';
const CONFLICT_ERROR = 'Este perfil foi alterado por outra pessoa. Recarregue para conferir a situação atual.';
const SAVE_ERROR = 'Não foi possível salvar as permissões do perfil.';

export const AVISO_ADMINISTRATIVO =
  'Quem tiver este perfil poderá gerenciar usuários, perfis e permissões. Só o perfil Admin exige verificação em duas etapas no login.';
export const AVISO_SALVO =
  'Permissões salvas. Quem usa este perfil já segue as novas regras na próxima ação; o menu dessas pessoas se atualiza quando recarregarem a página.';

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';
const BOTAO_PRIMARIO = `min-h-[44px] rounded bg-primary px-4 text-sm font-semibold text-primary-foreground ${FOCO} disabled:cursor-not-allowed disabled:opacity-50`;
const BOTAO_SECUNDARIO = `min-h-[44px] rounded border border-border bg-background px-4 text-sm font-semibold text-foreground ${FOCO} disabled:cursor-not-allowed disabled:opacity-50`;
const BOTAO_TEXTO = `min-h-[44px] rounded px-2 text-sm font-semibold text-foreground underline-offset-4 hover:underline ${FOCO} disabled:cursor-not-allowed disabled:opacity-50`;

/** Em 409 usa a mensagem do servidor (ex.: trava anti lock-out); sem mensagem, é conflito de versão. */
export function mensagemDeErroAoSalvar(status: number, corpo: unknown): string {
  if (status === 409) {
    const mensagem = typeof corpo === 'object' && corpo !== null && 'message' in corpo ? (corpo).message : undefined;
    return typeof mensagem === 'string' && mensagem.trim().length > 0 ? mensagem : CONFLICT_ERROR;
  }
  return SAVE_ERROR;
}

function parsePermission(raw: unknown): PermissionMatrixItem | null {
  if (typeof raw !== 'string') return null;
  const separator = raw.indexOf(':');
  if (separator <= 0 || separator === raw.length - 1) return null;
  return { key: raw, module: raw.slice(0, separator), action: raw.slice(separator + 1) };
}

function isResponse(value: unknown): value is { readonly permissions: unknown; readonly version: number } {
  return typeof value === 'object' && value !== null && 'permissions' in value && 'version' in value && typeof value.version === 'number';
}

function hasPermissions(value: unknown): value is { readonly permissions: unknown } {
  return typeof value === 'object' && value !== null && 'permissions' in value;
}

function idSeguro(chave: string): string {
  return chave.replace(/[^a-zA-Z0-9_-]/g, '-');
}

/** Checkbox nativo com o terceiro estado (indeterminate) para "Marcar todos" do módulo. */
function CheckboxDoModulo({ estado, rotulo, disabled, onChange }: {
  readonly estado: EstadoDoModulo;
  readonly rotulo: string;
  readonly disabled: boolean;
  readonly onChange: () => void;
}): ReactElement {
  return (
    <label className="flex min-h-[44px] min-w-[44px] shrink-0 cursor-pointer items-center justify-center rounded focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring" title={rotulo}>
      <input
        type="checkbox"
        aria-label={rotulo}
        data-estado={estado}
        checked={estado === 'todos'}
        disabled={disabled}
        ref={(elemento) => { if (elemento) elemento.indeterminate = estado === 'parcial'; }}
        onChange={onChange}
        className="h-5 w-5 rounded border-border accent-primary"
      />
    </label>
  );
}

function ErroDaMatriz({ error, onReload }: { readonly error: string; readonly onReload?: () => void }): ReactElement {
  return (
    <div className="rounded-md border border-danger p-4 text-sm text-danger" role="alert">
      <p>{error}</p>
      {onReload ? <button type="button" className={`mt-2 min-h-[44px] rounded px-4 font-semibold underline ${FOCO}`} onClick={onReload}>
        {error === CONFLICT_ERROR ? 'Recarregar situação atual' : 'Tentar novamente'}
      </button> : null}
    </div>
  );
}

export function PermissionMatrixContent({
  roleName, catalog, grants, partialGrants, error, saving, saveEnabled, salvo, avisoAdministrativo, variante = 'dialogo',
  alteracoes = 0, buscaInicial = '', soConcedidasInicial = false, onToggle, onToggleModulo, onSave, onDescartar, onReload,
}: PermissionMatrixContentProps): ReactElement {
  const grupos = useMemo(() => agruparPorModulo(catalog.map((item) => item.key)), [catalog]);
  const [busca, setBusca] = useState(buscaInicial);
  const [soConcedidas, setSoConcedidas] = useState(soConcedidasInicial);
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(() => {
    const iniciais = new Set(modulosAbertosIniciais(grupos, grants));
    if (filtroAtivo(buscaInicial, soConcedidasInicial)) {
      for (const grupo of filtrarPermissoes(grupos, buscaInicial, soConcedidasInicial, grants)) iniciais.add(grupo.modulo);
    }
    return iniciais;
  });
  const [descricoesAbertas, setDescricoesAbertas] = useState<ReadonlySet<string>>(() => new Set());

  const visiveis = filtrarPermissoes(grupos, busca, soConcedidas, grants);
  const filtrando = filtroAtivo(busca, soConcedidas);
  const grantedCount = catalog.filter((item) => grants[item.key] === true).length;
  const mostrarAvisoAdmin = avisoAdministrativo ?? (grants['users:manage'] === true && roleName !== 'Admin');
  const painel = variante === 'painel';
  const sujo = alteracoes > 0;
  const erroNaBarra = painel && sujo && Boolean(error);

  function abrirCorrespondentes(proximaBusca: string, proximoSoConcedidas: boolean): void {
    if (!filtroAtivo(proximaBusca, proximoSoConcedidas)) return;
    const correspondentes = filtrarPermissoes(grupos, proximaBusca, proximoSoConcedidas, grants).map((grupo) => grupo.modulo);
    setAbertos((atual) => new Set([...atual, ...correspondentes]));
  }

  function alternarModulo(modulo: string, aberto: boolean): void {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (aberto) proximo.add(modulo); else proximo.delete(modulo);
      return proximo;
    });
  }

  function alternarDescricao(chave: string): void {
    setDescricoesAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave); else proximo.add(chave);
      return proximo;
    });
  }

  const barraDeAcoes = (
    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
      {saving ? <span className="w-full text-sm text-muted-foreground sm:w-auto" role="status">Salvando permissões…</span> : null}
      {sujo && onDescartar ? <button type="button" onClick={onDescartar} disabled={saving} className={`${BOTAO_SECUNDARIO} flex-1 sm:flex-none`}>Descartar</button> : null}
      <button type="button" onClick={onSave} disabled={!saveEnabled} className={`${BOTAO_PRIMARIO} flex-1 sm:flex-none`}>
        {painel ? 'Salvar permissões' : 'Salvar permissões do perfil'}
      </button>
    </div>
  );

  return (
    <section
      className={painel ? 'permission-matrix relative flex min-w-0 flex-col gap-4' : 'permission-matrix relative flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground'}
      data-testid="permission-matrix-tablet"
      aria-label={`Permissões do perfil ${roleName}`}
    >
      {painel ? null : (
        <header className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">Matriz de permissões</h3>
          <p className="break-words text-sm text-muted-foreground" data-testid="permission-matrix-long-text" title={roleName}>Perfil: {roleName}</p>
        </header>
      )}
      {error && !erroNaBarra ? <ErroDaMatriz error={error} onReload={onReload} /> : null}
      {salvo && !error && !sujo ? <div className="rounded-md border border-success p-4 text-sm text-foreground" role="status">
        {AVISO_SALVO}
      </div> : null}
      {mostrarAvisoAdmin ? <div className="rounded-md border border-warning p-4 text-sm text-foreground" role="status" data-testid="permission-matrix-aviso-admin">
        {AVISO_ADMINISTRATIVO}
      </div> : null}
      {partialGrants.length > 0 ? <div className="rounded-md border border-warning p-4 text-sm text-foreground" role="status">
        <strong>Permissões legadas preservadas.</strong> Este perfil também possui {partialGrants.join(', ')}; elas não são removidas por esta edição.
      </div> : null}

      {catalog.length === 0 ? <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="permission-matrix-empty">
        Nenhuma permissão foi concedida a este perfil. O catálogo ficará disponível quando a conexão for restabelecida.
      </div> : (
        <>
          <div className="flex flex-col gap-2" data-testid="permission-matrix-ferramentas">
            <div className="flex flex-wrap items-center gap-2">
              <label className="relative min-w-0 flex-1 basis-56">
                <span className="sr-only">Buscar permissão</span>
                <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={busca}
                  placeholder="Buscar permissão por nome, descrição ou chave"
                  onChange={(evento) => { setBusca(evento.target.value); abrirCorrespondentes(evento.target.value, soConcedidas); }}
                  className={`min-h-[44px] w-full rounded border border-input bg-background pl-8 pr-4 text-sm text-foreground ${FOCO}`}
                />
              </label>
              <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded px-2 text-sm text-foreground focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
                <input
                  type="checkbox"
                  checked={soConcedidas}
                  onChange={(evento) => { setSoConcedidas(evento.target.checked); abrirCorrespondentes(busca, evento.target.checked); }}
                  className="h-5 w-5 rounded border-border accent-primary"
                />
                Só concedidas
              </label>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground" aria-live="polite">
                {grantedCount === 0 ? 'Nenhuma permissão foi concedida a este perfil.' : `${grantedCount} de ${catalog.length} permissões concedidas`}
              </p>
              <div className="flex gap-1">
                <button type="button" className={BOTAO_TEXTO} onClick={() => setAbertos(new Set(grupos.map((grupo) => grupo.modulo)))}>Expandir tudo</button>
                <button type="button" className={BOTAO_TEXTO} onClick={() => setAbertos(new Set())}>Recolher tudo</button>
              </div>
            </div>
          </div>

          {visiveis.length === 0 ? <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="permission-matrix-sem-resultado">
            {soConcedidas && !busca.trim() ? 'Nenhuma permissão concedida a este perfil.' : 'Nenhuma permissão corresponde à busca.'}
          </p> : null}

          <div className="flex flex-col gap-2" data-testid="permission-matrix-catalog">
            {visiveis.map((grupo) => {
              const completo = grupos.find((item) => item.modulo === grupo.modulo) ?? grupo;
              const contagem = contagemDoModulo(completo.itens, grants);
              const exibidos = contagemDoModulo(grupo.itens, grants);
              const aberto = abertos.has(grupo.modulo);
              const marcar = exibidos.estado !== 'todos';
              const rotuloMarcar = `${marcar ? 'Marcar todos' : 'Desmarcar todos'} em ${grupo.rotulo}${filtrando ? ' (exibidos)' : ''}`;
              return (
                <Collapsible
                  key={grupo.modulo}
                  open={aberto}
                  onOpenChange={(proximo) => alternarModulo(grupo.modulo, proximo)}
                  className="min-w-0 rounded-md border border-border bg-card text-card-foreground"
                  data-testid="permission-matrix-modulo"
                  data-modulo={grupo.modulo}
                >
                  <div className="flex items-center gap-1 pr-1">
                    <CollapsibleTrigger className={`flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded px-2 text-left ${FOCO}`}>
                      <ChevronRight size={16} aria-hidden="true" className={`shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${aberto ? 'rotate-90' : ''}`} />
                      <span className="min-w-0 flex-1 break-words text-sm font-semibold text-foreground">{grupo.rotulo}</span>
                      <span className="shrink-0 text-sm text-muted-foreground" data-testid="permission-matrix-contador" aria-label={`${contagem.concedidas} de ${contagem.total} concedidas`}>
                        {`${contagem.concedidas}/${contagem.total}`}
                      </span>
                    </CollapsibleTrigger>
                    <CheckboxDoModulo
                      estado={exibidos.estado}
                      rotulo={rotuloMarcar}
                      disabled={saving || !onToggleModulo}
                      onChange={() => onToggleModulo?.(grupo.itens.map((item) => item.chave), marcar)}
                    />
                  </div>
                  <CollapsibleContent forceMount hidden={!aberto} className="border-t border-border data-[state=closed]:hidden">
                    <ul className="m-0 grid list-none gap-x-4 p-1 sm:grid-cols-2 xl:grid-cols-3">
                      {grupo.itens.map((item) => {
                        const idDescricao = `${idSeguro(item.chave)}-description`;
                        const descricaoAberta = descricoesAbertas.has(item.chave);
                        return (
                          <li key={item.chave} className="min-w-0">
                            <div className="flex min-h-[44px] items-center gap-1">
                              <label className="flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded px-2 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring" title={item.descricao}>
                                <input type="checkbox" checked={grants[item.chave] === true} disabled={saving} onChange={() => onToggle(item.chave)} aria-describedby={idDescricao} className="h-5 w-5 shrink-0 rounded border-border accent-primary" />
                                <span className="min-w-0 break-words text-sm text-foreground">{item.rotuloAcao}</span>
                              </label>
                              <button
                                type="button"
                                aria-expanded={descricaoAberta}
                                aria-controls={idDescricao}
                                aria-label={`${descricaoAberta ? 'Ocultar' : 'Mostrar'} descrição de ${item.rotuloAcao}`}
                                onClick={() => alternarDescricao(item.chave)}
                                className={`flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground ${FOCO}`}
                              >
                                <Info size={16} aria-hidden="true" />
                              </button>
                            </div>
                            <p id={idDescricao} className={descricaoAberta ? 'break-words px-2 pb-2 pl-8 text-xs text-muted-foreground' : 'sr-only'}>
                              {item.descricao}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </>
      )}

      {painel ? (
        sujo || saving ? (
          <div className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-2 border-t border-border bg-card px-4 py-2 sm:flex-row sm:items-center sm:justify-between" data-testid="permission-matrix-barra" role="region" aria-label="Alterações não salvas">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-sm font-semibold text-foreground" aria-live="polite">{rotuloAlteracoes(alteracoes)}</span>
              {erroNaBarra && error ? <ErroDaMatriz error={error} onReload={onReload} /> : null}
            </div>
            {barraDeAcoes}
          </div>
        ) : null
      ) : (
        <footer className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">{sujo ? rotuloAlteracoes(alteracoes) : ''}</span>
          {barraDeAcoes}
        </footer>
      )}
    </section>
  );
}

function PermissionMatrixSkeleton({ roleName }: { readonly roleName: string }): ReactElement {
  return <section className="min-w-0 rounded-lg border border-border bg-card p-4" aria-busy="true" data-testid="permission-matrix-skeleton">
    <p className="mb-4 text-sm text-muted-foreground">Carregando permissões de {roleName}...</p>
    <div className="space-y-2">{Array.from({ length: 8 }, (_, index) => <div key={index} className="h-11 animate-pulse rounded bg-muted motion-reduce:animate-none" data-testid="permission-matrix-skeleton-row" />)}</div>
  </section>;
}

export function PermissionMatrix({ roleId, roleName, onSaveSuccess, variante = 'dialogo', onAlteracoes }: PermissionMatrixProps): ReactElement {
  const [catalog, setCatalog] = useState<readonly PermissionMatrixItem[]>([]);
  const [grants, setGrants] = useState<Record<string, boolean>>({});
  const [salvas, setSalvas] = useState<Record<string, boolean>>({});
  const [partialGrants, setPartialGrants] = useState<readonly string[]>([]);
  const [version, setVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [salvo, setSalvo] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let active = true;
    async function loadPermissions(): Promise<void> {
      setLoading(true); setError(null); setSalvo(false);
      try {
        const [catalogRes, grantsRes] = await Promise.all([
          fetch('/api/v1/authorization/permissions', { credentials: 'include' }),
          fetch(`/api/v1/authorization/roles/${roleId}/permissions`, { credentials: 'include' }),
        ]);
        if (!catalogRes.ok || !grantsRes.ok) throw new Error(LOAD_ERROR);
        const catalogBody: unknown = await catalogRes.json();
        const grantsBody: unknown = await grantsRes.json();
        if (!hasPermissions(catalogBody) || !Array.isArray(catalogBody.permissions) || !isResponse(grantsBody) || !Array.isArray(grantsBody.permissions) || !Number.isInteger(grantsBody.version) || grantsBody.version < 1) throw new Error(LOAD_ERROR);
        if (!active) return;
        const parsed = catalogBody.permissions.map(parsePermission).filter((item): item is PermissionMatrixItem => item !== null);
        const known = new Set(parsed.map((item) => item.key));
        const granted = grantsBody.permissions.filter((item): item is string => typeof item === 'string');
        const nextGrants: Record<string, boolean> = {};
        for (const item of parsed) nextGrants[item.key] = granted.includes(item.key);
        setCatalog(parsed); setGrants(nextGrants); setSalvas(nextGrants); setPartialGrants(granted.filter((item) => !known.has(item)).sort((left, right) => left.localeCompare(right, 'pt-BR'))); setVersion(grantsBody.version);
      } catch {
        if (active) setError(LOAD_ERROR);
      } finally {
        if (active) setLoading(false);
      }
    }
    void loadPermissions();
    return () => { active = false; };
  }, [roleId, reloadCount]);

  const alteracoes = useMemo(() => chavesAlteradas(salvas, grants).length, [salvas, grants]);
  useEffect(() => { onAlteracoes?.(alteracoes); }, [alteracoes, onAlteracoes]);
  useEffect(() => () => { onAlteracoes?.(0); }, [onAlteracoes]);

  const saveEnabled = useMemo(() => !saving && version !== null && catalog.length > 0, [catalog.length, saving, version]);
  function toggleGrant(key: string): void {
    if (saving) return;
    setSalvo(false);
    setGrants((current) => ({ ...current, [key]: current[key] !== true }));
  }
  function toggleModulo(keys: readonly string[], marcar: boolean): void {
    if (saving) return;
    setSalvo(false);
    setGrants((current) => marcarTodas(current, keys, marcar));
  }
  function descartar(): void {
    if (saving) return;
    setSalvo(false); setError(null);
    setGrants(descartarAlteracoes(salvas));
  }
  async function save(): Promise<void> {
    if (!saveEnabled || version === null) return;
    setSaving(true); setError(null); setSalvo(false);
    const enviadas = { ...grants };
    try {
      const response = await apiMutate(`/api/v1/authorization/roles/${roleId}/permissions`, { method: 'PUT', body: { version, grants: catalog.filter((item) => enviadas[item.key] === true).map((item) => ({ module: item.module, action: item.action })) } });
      if (!response.ok) {
        const corpo: unknown = await response.json().catch(() => ({}));
        throw new Error(mensagemDeErroAoSalvar(response.status, corpo));
      }
      const body: unknown = await response.json();
      if (!isResponse(body) || !Number.isInteger(body.version) || body.version < 1) throw new Error(LOAD_ERROR);
      setVersion(body.version); setSalvas(enviadas); setSalvo(true); onSaveSuccess?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : SAVE_ERROR);
    } finally { setSaving(false); }
  }

  if (loading) return <PermissionMatrixSkeleton roleName={roleName} />;
  return (
    <PermissionMatrixContent
      roleName={roleName}
      catalog={catalog}
      grants={grants}
      partialGrants={partialGrants}
      error={error}
      saving={saving}
      saveEnabled={saveEnabled}
      salvo={salvo}
      variante={variante}
      alteracoes={alteracoes}
      onToggle={toggleGrant}
      onToggleModulo={toggleModulo}
      onSave={() => void save()}
      onDescartar={descartar}
      onReload={() => setReloadCount((count) => count + 1)}
    />
  );
}
